import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Autocomplete,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table';
import {
  beaconConcernsApi,
  BeaconConcern,
  BeaconConcernFilters,
  BeaconConcernType,
  beaconAssignmentsApi,
  locationsApi,
  locationAssignmentsApi,
  locationEquivalenciesApi,
} from '../api/services';
import { extractApiError } from '../utils/apiErrors';
import type { PaginationParams } from '../api/config';
import { ConcernDrawer } from '../components/concerns/ConcernDrawer';

const CONCERN_TYPE_LABEL: Record<BeaconConcernType, string> = {
  unassigned_visitor: 'No Location Assigned',
  misassigned_beacon: 'Location Mismatch',
  lpa_config_gap:     'Missing LPA',
  sku_drift:          'SKU Drift',
};

const useDebouncedFilters = (initial: BeaconConcernFilters, delay = 300) => {
  const [filters, setFilters] = useState<BeaconConcernFilters>(initial);
  const [debounced, setDebounced] = useState<BeaconConcernFilters>(initial);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(filters), delay);
    return () => clearTimeout(t);
  }, [filters, delay]);
  return { filters, setFilters, debouncedFilters: debounced };
};

// --- Create-LPA dialog -----------------------------------------------------
// The group max is optional and LPE-grain: edits are saved to the location's
// LPE for the SKU's equivalency group (the only max grain — sc-5845), not to
// the LPA. Ungrouped SKUs have no LPE, so no max input is offered for them.

interface CreateLpaDialogProps {
  open: boolean;
  locationId: number;
  sku: string;
  onClose: () => void;
  onSubmit: (maxCount: number | null, productEquivalencyId: number | null) => Promise<void> | void;
  isLoading?: boolean;
  error?: string;
}

const CreateLpaDialog: React.FC<CreateLpaDialogProps> = ({
  open, locationId, sku, onClose, onSubmit, isLoading, error,
}) => {
  const [maxCount, setMaxCount] = useState('');
  const [inheritedMax, setInheritedMax] = useState<number | null>(null);
  const [peId, setPeId] = useState<number | null>(null);
  const [peState, setPeState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // Reset before fetching so a reopen for a different concern can never
    // submit the previous concern's group id or prefill.
    setPeState('loading');
    setPeId(null);
    setInheritedMax(null);
    setMaxCount('');
    locationEquivalenciesApi.maxForSku(locationId, sku)
      .then((res) => {
        if (cancelled) return;
        setPeId(res.product_equivalency_id ?? null);
        setInheritedMax(res.max_count ?? null);
        setMaxCount(res.max_count != null ? String(res.max_count) : '');
        setPeState('loaded');
      })
      // On lookup failure the LPA can still be created — the warning below
      // points at the product equivalencies page for setting the group max.
      .catch(() => { if (!cancelled) setPeState('error'); });
    return () => { cancelled = true; };
  }, [open, locationId, sku]);

  const busy = isLoading || submitting;
  // Non-empty input must be a whole non-negative number (rejects '-5',
  // '2.5', '1e3' — parseInt would silently truncate the latter two).
  const maxInvalid = maxCount.trim() !== '' && !/^\d+$/.test(maxCount.trim());

  const resetState = () => { setMaxCount(''); setInheritedMax(null); setPeId(null); setPeState('loading'); };
  const handleClose = () => {
    if (busy) return; // backdrop/Escape parity with the disabled buttons
    resetState();
    onClose();
  };
  const handleSubmit = async () => {
    if (submitting || maxInvalid) return;
    const trimmed = maxCount.trim();
    const parsed = trimmed === '' ? null : parseInt(trimmed, 10);
    setSubmitting(true);
    try {
      // On success the parent closes the dialog; on failure it stays open
      // with the error and current field values so the operator can retry.
      // State is re-initialized by the open-effect on the next open.
      await onSubmit(peId != null ? parsed : null, peId);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create LPA for ({locationId}, {sku})</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {peState === 'loaded' && peId != null && (
          <TextField
            autoFocus margin="dense" label="Group max (total units, optional)" type="number" fullWidth
            value={maxCount} onChange={(e) => setMaxCount(e.target.value)} disabled={busy}
            inputProps={{ min: 0 }}
            error={maxInvalid}
            helperText={maxInvalid
              ? 'Enter a whole number of 0 or more'
              : inheritedMax != null
                ? `Current group max ${inheritedMax} — applies to the whole equivalency group at this location`
                : 'Applies to the whole equivalency group at this location; leave blank for no max'}
          />
        )}
        {peState === 'loaded' && peId == null && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This SKU is not in an equivalency group — no group max applies.
          </Typography>
        )}
        {peState === 'error' && (
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            Couldn't load group info — the group max can be set from the product equivalencies page.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={busy || peState === 'loading' || maxInvalid}>
          {busy ? 'Working…' : 'Create LPA'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------

const BeaconConcernsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const urlParams = new URLSearchParams(window.location.search);
  const initialLocationId = urlParams.get('location_id') || undefined;
  const { filters, setFilters, debouncedFilters } = useDebouncedFilters(
    initialLocationId ? { location_id: initialLocationId, status: 'open' } : { status: 'open' }
  );
  const [sorting, setSorting] = useState<SortingState>([{ id: 'last_observed_at', desc: true }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });

  const [lpaDialog, setLpaDialog] = useState<{
    open: boolean; locationId: number; sku: string;
    onSubmit: (maxCount: number | null, productEquivalencyId: number | null) => Promise<void>;
    isLoading?: boolean; error?: string;
  }>({ open: false, locationId: 0, sku: '', onSubmit: async () => {} });

  const [selectedConcern, setSelectedConcern] = useState<BeaconConcern | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'error' | 'success' }>(
    { open: false, message: '', severity: 'error' }
  );
  const showError = useCallback((message: string) => {
    setSnackbar({ open: true, message, severity: 'error' });
  }, []);

  const paginationParams: PaginationParams = useMemo(() => ({
    page: pagination.pageIndex + 1,
    per_page: pagination.pageSize,
    sort_by: sorting[0]?.id,
    sort_dir: sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
  }), [pagination.pageIndex, pagination.pageSize, sorting]);

  const { data: response, isLoading, isFetching } = useQuery({
    queryKey: ['beacon-concerns', debouncedFilters, pagination.pageIndex, pagination.pageSize, sorting[0]?.id, sorting[0]?.desc],
    queryFn: () => beaconConcernsApi.listPage(debouncedFilters, paginationParams),
    placeholderData: (prev) => prev,
    staleTime: 30000,
    gcTime: 300000,
  });

  const concerns = response?.data ?? [];
  const totalCount = response?.pagination?.total_count ?? 0;

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.getAllWithoutFilters,
    staleTime: 300000,
    gcTime: 1800000,
  });

  const locationLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    locations.forEach((loc) => { lookup[String(loc.id)] = loc.name; });
    return lookup;
  }, [locations]);

  React.useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [debouncedFilters]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['beacon-concerns'] });
    queryClient.invalidateQueries({ queryKey: ['beacon-concerns-summary'] });
  }, [queryClient]);

  // Clear error/loading too so nothing stale shows during the close fade.
  const closeLpaDialog = () => setLpaDialog((d) => ({ ...d, open: false, isLoading: false, error: undefined }));

  // --- Per-type row actions ------------------------------------------------
  //
  // Pattern: every remediation action mutates the underlying state (BA / LPA)
  // AND then resolves the concern with `manual_fixed` so the user gets immediate
  // UI feedback. If the action didn't actually clear the detection condition,
  // the next detection tick reopens the concern — that's the system honestly
  // reporting state, not a regression.

  const resolveAfter = useCallback(async (concernId: number, note: string) => {
    try {
      await beaconConcernsApi.resolve(concernId, 'manual_fixed', note);
    } catch (e) {
      // Mutation already succeeded; concern will auto-resolve on next tick.
      // Don't surface this as an error — it's a non-event from the user's POV.
    }
    invalidate();
  }, [invalidate]);

  const handleAssignToObserved = useCallback(async (c: BeaconConcern) => {
    try {
      await beaconAssignmentsApi.assignToLocation(c.beacon_id, c.location_id);
      await resolveAfter(c.id, 'Assigned to observed location');
    } catch (e) {
      showError(extractApiError(e, 'Assignment failed'));
    }
  }, [resolveAfter, showError]);

  const handleReassignToObserved = useCallback(async (c: BeaconConcern) => {
    try {
      await beaconAssignmentsApi.reassignToLocation(c.beacon_id, c.location_id);
      await resolveAfter(c.id, 'Reassigned to observed location');
    } catch (e) {
      showError(extractApiError(e, 'Reassignment failed'));
    }
  }, [resolveAfter, showError]);

  const openCreateLpa = useCallback((c: BeaconConcern) => {
    setLpaDialog({
      open: true,
      locationId: c.location_id,
      sku: c.sku,
      onSubmit: async (maxCount, productEquivalencyId) => {
        try {
          setLpaDialog((d) => ({ ...d, isLoading: true, error: undefined }));
          await locationAssignmentsApi.create({ location_id: c.location_id, sku: c.sku });
        } catch (e) {
          setLpaDialog((d) => ({ ...d, isLoading: false, error: extractApiError(e, 'Create LPA failed') }));
          return;
        }
        // A new LPA exists — refresh the assignment-list caches other pages
        // read (assignments list, all-assignments panel query, dashboard).
        queryClient.invalidateQueries({ queryKey: ['location_assignments'] });
        queryClient.invalidateQueries({ queryKey: ['location_assignments_all'] });
        queryClient.invalidateQueries({ queryKey: ['location-assignments'] });

        // The LPA exists from here on — a group-max failure must not read as
        // a failed create (split handling, like LocationAssignmentsPage).
        const maxAttempted = maxCount != null && productEquivalencyId != null;
        let maxApplied = false;
        if (maxAttempted) {
          try {
            await locationEquivalenciesApi.upsert({
              location_id: c.location_id,
              product_equivalency_id: productEquivalencyId,
              max_count: maxCount,
            });
            maxApplied = true;
            queryClient.invalidateQueries({ queryKey: ['location_equivalencies'] });
          } catch (e) {
            showError(`LPA created, but saving the group max failed (${extractApiError(e, 'unknown error')}) — set it from the product equivalencies page`);
          }
        }
        // The note is a durable audit record on the concern — it must not
        // claim "(no max)" when an existing group max stays in effect, nor
        // claim a set when the upsert failed.
        const note = maxApplied
          ? `LPA created; group max set to ${maxCount}`
          : maxAttempted
            ? 'LPA created (group max save failed)'
            : 'LPA created';
        await resolveAfter(c.id, note);
        closeLpaDialog();
      },
    });
  }, [resolveAfter, showError, queryClient]);

  // sku_drift has no row actions — it's engineering-only. Resolution comes
  // from a SKU rename / BIS backfill, after which detection auto-resolves.
  // Bugsnag→Slack already pings engineering on open/reopen.

  // --- Columns -------------------------------------------------------------

  const columns = useMemo<ColumnDef<BeaconConcern>[]>(() => [
    {
      accessorKey: 'location_id', header: 'Observed at', size: 180,
      cell: ({ getValue }) => {
        const id = String(getValue() ?? '');
        return locationLookup[id] || id;
      },
    },
    {
      accessorKey: 'concern_type', header: 'Type', size: 170,
      cell: ({ getValue }) => CONCERN_TYPE_LABEL[getValue() as BeaconConcernType] ?? String(getValue()),
    },
    { accessorKey: 'sku', header: 'SKU', size: 140 },
    {
      accessorKey: 'status', header: 'Status', size: 130,
      cell: ({ getValue }) => {
        const s = getValue() as string;
        return (
          <Chip label={s} size="small"
            color={s === 'open' ? 'error' : s === 'acknowledged' ? 'warning' : 'success'} />
        );
      },
    },
    {
      accessorKey: 'last_observed_at', header: 'Last observed', size: 220,
      cell: ({ getValue }) => {
        const formatted = new Date(getValue() as string).toLocaleString(undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
        // Glue the AM/PM to the time so it can't wrap to a new line by itself.
        return formatted.replace(/\s+(AM|PM)$/i, ' $1');
      },
    },
    {
      id: 'details', header: 'Details', size: 220,
      cell: ({ row }) => {
        const c = row.original;
        if (c.concern_type === 'misassigned_beacon') {
          const aid = c.details?.assigned_location_id;
          return aid ? `Assigned to ${locationLookup[String(aid)] || aid}` : '—';
        }
        if (c.concern_type === 'sku_drift') {
          const bisSku = c.details?.bis_sku;
          return (
            <Tooltip title="Engineering has been notified via Bugsnag→Slack">
              <Box>
                <Typography variant="body2">BA: {c.sku}</Typography>
                <Typography variant="body2" color="text.secondary">BIS: {bisSku}</Typography>
              </Box>
            </Tooltip>
          );
        }
        return '—';
      },
      enableSorting: false,
    },
    {
      id: 'actions', header: 'Actions', size: 380,
      enableSorting: false,
      cell: ({ row }) => {
        const c = row.original;
        if (c.status === 'resolved') {
          return <Typography variant="caption" color="text.secondary">Resolved ({c.resolution_mode})</Typography>;
        }

        const stop = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };

        switch (c.concern_type) {
          case 'unassigned_visitor':
            return (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" onClick={stop(() => handleAssignToObserved(c))}>Assign here</Button>
              </Box>
            );
          case 'misassigned_beacon':
            return (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" onClick={stop(() => handleReassignToObserved(c))}>Reassign here</Button>
              </Box>
            );
          case 'lpa_config_gap':
            return (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" onClick={stop(() => openCreateLpa(c))}>Create LPA</Button>
              </Box>
            );
          case 'sku_drift':
            return (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Engineering notified — auto-resolves on rename
              </Typography>
            );
          default:
            return null;
        }
      },
    },
  ], [locationLookup, handleAssignToObserved, handleReassignToObserved, openCreateLpa]);

  const table = useReactTable({
    data: concerns,
    columns,
    state: { sorting, pagination },
    onSortingChange: (updater) => {
      setSorting(updater);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    autoResetPageIndex: false,
  });

  const handleFilterChange = useCallback(
    <K extends keyof BeaconConcernFilters>(key: K, value: BeaconConcernFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [setFilters]
  );

  return (
    <Box sx={{ height: '100%', width: '100%', p: 2 }}>
      <Typography variant="h4" gutterBottom>Tag Concerns</Typography>

      {(isLoading || isFetching) && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {isLoading ? 'Loading concerns…' : 'Updating filters…'}
          </Typography>
        </Box>
      )}

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <FormControl sx={{ minWidth: 200, flex: 1 }}>
          <InputLabel>Concern Type</InputLabel>
          <Select
            value={filters.type || ''}
            onChange={(e) => handleFilterChange('type', e.target.value as BeaconConcernType | '')}
            label="Concern Type"
          >
            <MenuItem value="">All</MenuItem>
            {(Object.keys(CONCERN_TYPE_LABEL) as BeaconConcernType[]).map((t) => (
              <MenuItem key={t} value={t}>{CONCERN_TYPE_LABEL[t]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200, flex: 1 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value as any)}
            label="Status"
          >
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="">All</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200, flex: 1 }}>
          <Autocomplete
            options={locations}
            getOptionLabel={(opt) => opt.name}
            value={locations.find((l) => String(l.id) === filters.location_id) || null}
            onChange={(_, v) => handleFilterChange('location_id', v ? String(v.id) : '')}
            renderInput={(params) => <TextField {...params} label="Location" />}
          />
        </FormControl>
        <FormControl sx={{ minWidth: 200, flex: 1 }}>
          <TextField
            label="Tag ID"
            value={filters.beacon_id || ''}
            onChange={(e) => handleFilterChange('beacon_id', e.target.value)}
          />
        </FormControl>
      </Box>


      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder ? null : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{ asc: ' ↑', desc: ' ↓' }[header.column.getIsSorted() as string] ?? null}
                        </Box>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => setSelectedConcern(row.original)}
                  sx={{ cursor: 'pointer' }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {totalCount === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1} to{' '}
            {Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalCount)} of {totalCount} total rows
          </Typography>
          <Typography variant="body2" color="text.secondary">
            click row to open history
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={pagination.pageSize}
                onChange={(e) => setPagination((p) => ({ ...p, pageSize: Number(e.target.value), pageIndex: 0 }))}
              >
                <MenuItem value={10}>10 per page</MenuItem>
                <MenuItem value={25}>25 per page</MenuItem>
                <MenuItem value={50}>50 per page</MenuItem>
                <MenuItem value={100}>100 per page</MenuItem>
              </Select>
            </FormControl>
            <Button size="small" onClick={() => setPagination((p) => ({ ...p, pageIndex: p.pageIndex - 1 }))}
                    disabled={pagination.pageIndex === 0}>Previous</Button>
            <Typography variant="body2">
              Page {pagination.pageIndex + 1} of {Math.max(1, Math.ceil(totalCount / pagination.pageSize))}
            </Typography>
            <Button size="small" onClick={() => setPagination((p) => ({ ...p, pageIndex: p.pageIndex + 1 }))}
                    disabled={pagination.pageIndex >= Math.ceil(totalCount / pagination.pageSize) - 1}>Next</Button>
          </Box>
        </Box>
      </Paper>

      <CreateLpaDialog
        open={lpaDialog.open}
        locationId={lpaDialog.locationId}
        sku={lpaDialog.sku}
        onClose={closeLpaDialog}
        onSubmit={lpaDialog.onSubmit}
        isLoading={lpaDialog.isLoading}
        error={lpaDialog.error}
      />

      <ConcernDrawer
        concern={selectedConcern}
        locationLookup={locationLookup}
        onClose={() => setSelectedConcern(null)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BeaconConcernsPage;
